import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useFocusMode } from '@/hooks/use-focus-mode'
import { cn } from '@/lib/utils'
import { Maximize2, Minimize2, X } from 'lucide-react'

interface FocusModeToggleProps {
  className?: string
}

/**
 * Toggle button for Focus Mode
 * Shows in header to enter focus mode, and as a floating button to exit
 */
export const FocusModeToggle = ({ className }: FocusModeToggleProps) => {
  const { isFocusMode, toggleFocusMode } = useFocusMode()

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant='outline'
          size='icon'
          onClick={toggleFocusMode}
          className={cn('cursor-pointer', className)}
          aria-label={isFocusMode ? 'Exit Focus Mode' : 'Enter Focus Mode'}
        >
          {isFocusMode ? (
            <Minimize2 className='h-[1.2rem] w-[1.2rem]' />
          ) : (
            <Maximize2 className='h-[1.2rem] w-[1.2rem]' />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{isFocusMode ? 'Exit Focus Mode (Esc)' : 'Focus Mode (F11)'}</p>
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Floating exit button that appears in focus mode
 * Fixed position in top-right corner for easy exit
 */
export const FocusModeExitButton = () => {
  const { isFocusMode, exitFocusMode } = useFocusMode()

  if (!isFocusMode) return null

  return (
    <div className='fixed top-4 right-4 z-50 animate-in fade-in slide-in-from-top-2 duration-300'>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='secondary'
            size='sm'
            onClick={exitFocusMode}
            className='gap-2 shadow-lg border border-border/50 backdrop-blur-sm bg-background/80 hover:bg-background'
          >
            <X className='h-4 w-4' />
            <span className='hidden sm:inline'>Exit Focus Mode</span>
            <kbd className='hidden sm:inline-flex pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground'>
              Esc
            </kbd>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Exit Focus Mode (Esc)</p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export default FocusModeToggle
